import type {
  PurchaseWebContract,
  PurchaseWebCurrency,
} from '@xing-com/crate-common-graphql-types';
import { PurchaseOrderType } from '@xing-com/crate-common-graphql-types';

export interface ContractPrice {
  currency: PurchaseWebCurrency;
  discount: number;
  totalSavings: number;
  current: number | null;
  initial: { total: number; monthly: number };
  renewal: { total: number; monthly: number };
}

export type Contract = Exclude<PurchaseWebContract, '__typename'>;

export function getContractPrice(contract: Contract): ContractPrice {
  const { initialDuration, renewalDuration, price } = contract;
  const runningContractPrice = price.current ?? 0;
  const monthlyInitialPrice = price.initial / initialDuration;
  const monthlyRenewalPrice = price.renewal / renewalDuration;

  // for "first time contracts" we consider monthlyRenewalPrice as the current price
  // for "renewal contracts" price.current is the monthly price of their running contract
  const withoutDiscount =
    contract.orderType === PurchaseOrderType.Upsell
      ? monthlyRenewalPrice
      : runningContractPrice;
  const monthlySavings = withoutDiscount - monthlyInitialPrice;
  const totalSavings = monthlySavings * initialDuration;

  // In case of revoke we want to use price.current instead
  const discountBase =
    contract.orderType === PurchaseOrderType.Upsell
      ? monthlyRenewalPrice
      : runningContractPrice;
  const discount = floorToBase(monthlySavings / discountBase);

  return {
    currency: price.currency,
    discount,
    totalSavings: toMajorCurrency(totalSavings),
    initial: {
      total: toMajorCurrency(monthlyInitialPrice * initialDuration),
      monthly: toMajorCurrency(monthlyInitialPrice),
    },
    renewal: {
      total: toMajorCurrency(monthlyRenewalPrice * renewalDuration),
      monthly: toMajorCurrency(monthlyRenewalPrice),
    },
    current:
      contract.orderType === PurchaseOrderType.Upsell
        ? toMajorCurrency(runningContractPrice)
        : null,
  };
}

function floorToBase(discount: number, base = 5): number {
  return (Math.floor((discount * 100) / base) * base) / 100;
}

function toMajorCurrency(minorCurrency: number): number {
  return minorCurrency / 100;
}
